import _ from "lodash";
import { isPartialSlotMapProp, mergePartialSlotMap } from "./localizationOverrides";

const IFRAME_ID = "gamePreviewIframe";
const GAME_CONFIG_PREFIX = "gameConfig.";
const COMPONENTS_PREFIX = "gameConfig.components.";
const OVERRIDES_PATH_REGEX =
  /^gameConfig\.localizationOverrides\.([^.]+)\.([^.]+)\.([^.[]+)/;

/**
 * Live-drag preview: ship a minimal partial config straight to the playable,
 * bypassing the per-tick version cloneDeep + full settings-tree React render
 * that makes large configs (EPCG) stutter while dragging.
 *
 * The playable's applyGameConfig merges per component property and replaces
 * each top-level property wholesale, so callers must send complete top-level
 * values (see buildComponentPatch).
 */
export function pushPreviewPatch(patch) {
  if (typeof window !== "undefined" && window.__disablePreview) return;
  const iframe = document.getElementById(IFRAME_ID);
  const contentWindow = iframe && iframe.contentWindow;
  if (!contentWindow || !_.isFunction(contentWindow.applyGameConfig)) return;
  try {
    // Clone so the playable cannot mutate dashboard version state.
    contentWindow.applyGameConfig(_.cloneDeep(patch));
  } catch (err) {
    console.trace(err);
  }
}

function cloneOptions(version) {
  return _.cloneDeep(_.get(version, "gameConfig.options", {}));
}

/**
 * Build a partial config for a `gameConfig.components.<id>.<propPath>` write.
 * Rebuilds the full top-level property value from `version` and patches the
 * changed leaf inside it.
 *
 * `options` is always included: most playables read it as
 * `Object.entries(config.options)`, which throws on a config without it.
 * It is cloned so the playable cannot mutate dashboard version state.
 *
 * @param {object} version selected version carrying the current gameConfig
 * @param {string} fullPath e.g. "gameConfig.components.heroManager1.position.x"
 * @param {*} value new leaf value
 * @returns {object|null} partial config, or null when the path is not a
 *   component property write
 */
export function buildComponentPatch(version, fullPath, value) {
  const prefix = "gameConfig.components.";
  if (typeof fullPath !== "string" || !fullPath.startsWith(prefix)) return null;

  const rest = fullPath.slice(prefix.length);
  const dotIndex = rest.indexOf(".");
  if (dotIndex <= 0) return null;
  const componentId = rest.slice(0, dotIndex);
  const propPath = rest.slice(dotIndex + 1);
  const topMatch = propPath.match(/^[^.[]+/);
  if (!topMatch) return null;
  const topProp = topMatch[0];

  let topValue = value;
  if (propPath !== topProp) {
    topValue = _.cloneDeep(_.get(version, `${prefix}${componentId}.${topProp}`, {}));
    let subPath = propPath.slice(topProp.length);
    if (subPath.startsWith(".")) subPath = subPath.slice(1);
    _.set(topValue, subPath, value);
  }

  return {
    components: { [componentId]: { [topProp]: topValue } },
    options: cloneOptions(version)
  };
}

/**
 * Partial config for a version-path commit. Playables do not read
 * localizationOverrides; those writes are mapped to the equivalent component
 * property (slot maps merged with English so wholesale replace stays safe).
 *
 * Returns null when the path is not a safe partial (whole `gameConfig`, a
 * whole component, non-gameConfig fields, …) — callers should send the full
 * config in that case.
 *
 * @param {object} version version after the path has been written
 * @param {string} fullPath e.g. "gameConfig.components.hero.position.x"
 * @param {*} value leaf or top-level value written at that path
 * @returns {object|null}
 */
export function buildCommitPreviewPatch(version, fullPath, value) {
  if (typeof fullPath !== "string" || !fullPath.startsWith(GAME_CONFIG_PREFIX)) {
    return null;
  }
  if (fullPath === "gameConfig") return null;

  const componentPatch = buildComponentPatch(version, fullPath, value);
  if (componentPatch) return componentPatch;

  const overrideMatch = fullPath.match(OVERRIDES_PATH_REGEX);
  if (overrideMatch) {
    const componentId = overrideMatch[2];
    const topProp = overrideMatch[3];
    let topValue = value;
    if (isPartialSlotMapProp(topProp)) {
      const english = _.get(version, `${COMPONENTS_PREFIX}${componentId}.${topProp}`, {});
      topValue = mergePartialSlotMap(english, value);
    }
    return {
      components: { [componentId]: { [topProp]: topValue } },
      options: cloneOptions(version)
    };
  }

  // gameConfig.options.* is not a safe partial: playables merge options but
  // typically only re-render when `components` is present. Caller full-applies.
  return null;
}
